import axios from "axios";
import {
  IAlarm,
  ICamera,
  IDoorBuzzer,
  IOfficeKeys,
  IHVAC,
  ISafe,
  ICategory,
  ICardReader,
} from "../../../modules/CarrierAgreementMgmt/Security/types";
import moment from "moment";
const api_base_url = process.env.REACT_APP_API_BASEURL;

export const getSecurities = async () => {
  const data = await axios.get(`${api_base_url}/Security`);
  return data;
};

export const getSecuritiesCount = async () => {
  const data = await axios.get(`${api_base_url}/Security/GetCount`);
  return data;
};

const convertTo12Hour = (time24) => {
  if (!time24) return "";

  const [hours, minutes] = time24.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "pm" : "am";
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  return `${hour12.toString().padStart(2, "0")}:${minutes}${ampm}`;
};

const addFormattedHoursToGroups = (secCardReader) => {
  if (!secCardReader || !Array.isArray(secCardReader)) {
    return secCardReader;
  }

  return secCardReader.map((reader) => {
    if (!reader.groupInfos || !Array.isArray(reader.groupInfos)) {
      return reader;
    }

    const updatedGroupInfos = reader.groupInfos.map((group) => {
      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      const formattedHours = {};

      days.forEach((day) => {
        const startTimeKey = `${day}StartTime`;
        const endTimeKey = `${day}EndTime`;
        const startTime = group[startTimeKey];
        const endTime = group[endTimeKey];

        if (startTime && endTime) {
          const startTime12 = convertTo12Hour(startTime);
          const endTime12 = convertTo12Hour(endTime);
          formattedHours[`${day}_hours`] = `${startTime12}-${endTime12}`;
        } else {
          formattedHours[`${day}_hours`] = "";
        }
      });

      return {
        ...group,
        ...formattedHours,
      };
    });

    return {
      ...reader,
      groupInfos: updatedGroupInfos,
    };
  });
};

//Insert New Security
export const addSecurity = async (newSC) => {
  const updatedSC = {
    ...newSC,
    initialAddCameras: newSC.initialAddCameras,
    initialAddAlarms: newSC.initialAddAlarms,
    initialAddFireAlarmSystems: newSC.initialAddFireAlarmSystems,
    secCardReader: (newSC.secCardReader ?? []).map((reader) => {
      const combinedAdmins = [
        ...(reader.siteAdmins ?? []),
        ...(reader.masterAdmin ?? []).map((admin) => ({
          ...admin,
          isMasterAdmin: true,
        })),
      ];

      return {
        ...reader,
        siteAdmins: combinedAdmins,
        masterAdmin: undefined,
      };
    }),
  };

  const formData = new FormData();
  updatedSC.alarm?.forEach((obj: IAlarm, i: number) =>
    Object.entries(obj)?.forEach(([key, value]) => {
      if (value) {
        formData.append(`AlarmsDto[${i}].${key}`, value);
      }
    })
  );
  updatedSC.attachmentDisplay?.forEach((obj: ICategory, i: number) =>
    Object.entries(obj)?.forEach(([key, value]) => {
      if (value) {
        formData.append(`FileCatergoryDto[${i}].${key}`, value);
      }
    })
  );

  updatedSC.camera?.forEach((obj: ICamera, i: number) =>
    Object.entries(obj)?.forEach(([key, value]) => {
      if (value) {
        if (key === "camerasAndLocation") {
          value = value && value.map((x) => x.value)?.join(",");
        }
        formData.append(`CamerasDto[${i}].${key}`, value);
      }
    })
  );

  updatedSC.safe?.forEach((obj: ISafe, i: number) =>
    Object.entries(obj)?.forEach(([key, value]) => {
      if (value) {
        formData.append(`SafeDetailsDto[${i}].${key}`, value);
      }
    })
  );

  updatedSC.doorBuzzer?.forEach((obj: IDoorBuzzer, i: number) =>
    Object.entries(obj)?.forEach(([key, value]) => {
      if (value) {
        formData.append(`DoorBuzzersDto[${i}].${key}`, value);
      }
    })
  );

  updatedSC.officeKey?.forEach((obj: IOfficeKeys, i: number) =>
    Object.entries(obj)?.forEach(([key, value]) => {
      if (value) {
        if (key === "keysAssignedTo") {
          value = value && value.map((x) => x.value)?.join(",");
        }
        formData.append(`OfficeKeysDto[${i}].${key}`, value);
      }
    })
  );

  updatedSC.hvac?.forEach((obj: IHVAC, i: number) =>
    Object.entries(obj)?.forEach(([key, value]) => {
      if (value) {
        formData.append(`HvacsDto[${i}].${key}`, value);
      }
    })
  );

  updatedSC.camerasAndLocation =
    updatedSC.camerasAndLocation &&
    updatedSC.camerasAndLocation.map((x) => x.value)?.join(",");
  updatedSC.keysAssignedTo =
    updatedSC.keysAssignedTo &&
    updatedSC.keysAssignedTo.map((x) => x.value)?.join(",");

  updatedSC.officeId && formData.append("officeId", updatedSC.officeId);
  updatedSC.officeState &&
    formData.append("officeState", updatedSC.officeState);
  updatedSC.officeBrand &&
    formData.append("officeBrand", updatedSC.officeBrand);
  updatedSC.officeStatus &&
    formData.append("officeStatus", updatedSC.officeStatus);

  updatedSC.vendorId && formData.append("vendorId", updatedSC.vendorId);
  updatedSC.vendor && formData.append("vendor", updatedSC.vendor);
  updatedSC.vendorPhone &&
    formData.append("vendorPhone", updatedSC.vendorPhone);
  updatedSC.contractRenewalDate &&
    formData.append("contractRenewalDate", updatedSC.contractRenewalDate);
  updatedSC.accountNumber &&
    formData.append("accountNumber", updatedSC.accountNumber);
  updatedSC.alarmSiteNumber &&
    formData.append("alarmSiteNumber", updatedSC.alarmSiteNumber);
  updatedSC.dataConnectionId &&
    formData.append("dataConnectionId", updatedSC.dataConnectionId);
  updatedSC.alarmInstallDate &&
    formData.append("alarmInstallDate", updatedSC.alarmInstallDate);

  updatedSC.connectionDetails &&
    formData.append("connectionDetails", updatedSC.connectionDetails);
  updatedSC.alarmTypeId &&
    formData.append("alarmTypeId", updatedSC.alarmTypeId);
  updatedSC.numberOfMotionSensors &&
    formData.append("numberOfMotionSensors", updatedSC.numberOfMotionSensors);
  updatedSC.numberOfDoorContacts &&
    formData.append("numberOfDoorContacts", updatedSC.numberOfDoorContacts);
  updatedSC.numberOfPanicButtons &&
    formData.append("numberOfPanicButtons", updatedSC.numberOfPanicButtons);
  updatedSC.specialEquipment &&
    formData.append("specialEquipment", updatedSC.specialEquipment);
  updatedSC.camerasAndLocation &&
    formData.append("camerasAndLocation", updatedSC.camerasAndLocation);
  updatedSC.cameraType1Id &&
    formData.append("cameraType1Id", updatedSC.cameraType1Id);

  updatedSC.cameraType2Id &&
    formData.append("cameraType2Id", updatedSC.cameraType2Id);
  updatedSC.cameraMake && formData.append("cameraMake", updatedSC.cameraMake);
  updatedSC.cameraInstallDate &&
    formData.append("cameraInstallDate", updatedSC.cameraInstallDate);
  updatedSC.cameraModel &&
    formData.append("cameraModel", updatedSC.cameraModel);
  updatedSC.dvrTypeId && formData.append("dvrTypeId", updatedSC.dvrTypeId);
  updatedSC.dvrRecordingMax &&
    formData.append("dvrRecordingMax", updatedSC.dvrRecordingMax);
  updatedSC.cameraSystemId &&
    formData.append("cameraSystemId", updatedSC.cameraSystemId);
  updatedSC.safeTypeId && formData.append("safeTypeId", updatedSC.safeTypeId);
  updatedSC.isSafeBolted &&
    formData.append("isSafeBolted", updatedSC.isSafeBolted);

  updatedSC.safeCode && formData.append("safeCode", updatedSC.safeCode);
  updatedSC.safeMakeId && formData.append("safeMakeId", updatedSC.safeMakeId);
  updatedSC.safeModel && formData.append("safeModel", updatedSC.safeModel);
  updatedSC.safeSerialNumber &&
    formData.append("safeSerialNumber", updatedSC.safeSerialNumber);
  updatedSC.safeLocation &&
    formData.append("safeLocation", updatedSC.safeLocation);
  updatedSC.siteNumber && formData.append("siteNumber", updatedSC.siteNumber);
  updatedSC.connection && formData.append("connection", updatedSC.connection);
  updatedSC.installDate &&
    formData.append("installDate", updatedSC.installDate);

  updatedSC.safeCode && formData.append("safeCode", updatedSC.safeCode);
  updatedSC.safeMakeId && formData.append("safeMakeId", updatedSC.safeMakeId);
  updatedSC.safeModel && formData.append("safeModel", updatedSC.safeModel);
  updatedSC.safeSerialNumber &&
    formData.append("safeSerialNumber", updatedSC.safeSerialNumber);
  updatedSC.safeLocation &&
    formData.append("safeLocation", updatedSC.safeLocation);
  updatedSC.siteNumber && formData.append("siteNumber", updatedSC.siteNumber);
  updatedSC.connection && formData.append("connection", updatedSC.connection);
  updatedSC.installDate &&
    formData.append("installDate", updatedSC.installDate);

  updatedSC.type && formData.append("type", updatedSC.type);
  updatedSC.numberOfButtonSelected &&
    formData.append("numberOfButtonSelected", updatedSC.numberOfButtonSelected);
  updatedSC.warranty && formData.append("warranty", updatedSC.warranty);
  updatedSC.warrantyLength &&
    formData.append("warrantyLength", updatedSC.warrantyLength);
  updatedSC.warrantyCoverage &&
    formData.append("warrantyCoverage", updatedSC.warrantyCoverage);
  updatedSC.warrantyCoverageId &&
    formData.append("warrantyCoverageId", updatedSC.warrantyCoverageId);
  updatedSC.typeOfVendorId &&
    formData.append("typeOfVendorId", updatedSC.typeOfVendorId);
  updatedSC.dbinstallVendorId &&
    formData.append("dbinstallVendorId", updatedSC.dbinstallVendorId);

  updatedSC.dbinstallVendorName &&
    formData.append("dbinstallVendorName", updatedSC.dbinstallVendorName);
  updatedSC.dbinstallVendorAddress &&
    formData.append("dbinstallVendorAddress", updatedSC.dbinstallVendorAddress);
  updatedSC.dbinstallVendorPhone &&
    formData.append("dbinstallVendorPhone", updatedSC.dbinstallVendorPhone);
  updatedSC.contact && formData.append("contact", updatedSC.contact);
  updatedSC.lockSmithVendor &&
    formData.append("lockSmithVendor", updatedSC.lockSmithVendor);
  updatedSC.lockSmithVendorId &&
    formData.append("lockSmithVendorId", updatedSC.lockSmithVendorId);
  updatedSC.okvendorAddress &&
    formData.append("okvendorAddress", updatedSC.okvendorAddress);
  updatedSC.okvendorPhone &&
    formData.append("okvendorPhone", updatedSC.okvendorPhone);

  updatedSC.numberOfKeys &&
    formData.append("numberOfKeys", updatedSC.numberOfKeys);
  updatedSC.keysAssignedTo &&
    formData.append("keysAssignedTo", updatedSC.keysAssignedTo);
  updatedSC.keyCodingModel &&
    formData.append("keyCodingModel", updatedSC.keyCodingModel);
  updatedSC.numberOfUnits &&
    formData.append("numberOfUnits", updatedSC.numberOfUnits);
  updatedSC.unitType && formData.append("unitType", updatedSC.unitType);
  updatedSC.tonCapacity &&
    formData.append("tonCapacity", updatedSC.tonCapacity);
  updatedSC.hvacinstallDate &&
    formData.append("hvacinstallDate", updatedSC.hvacinstallDate);
  updatedSC.preventiveMaintenancePlan &&
    formData.append(
      "preventiveMaintenancePlan",
      updatedSC.preventiveMaintenancePlan
    );
  updatedSC.maintenanceCompanyId &&
    formData.append("maintenanceCompanyId", updatedSC.maintenanceCompanyId);
  updatedSC.maintenanceCompany &&
    formData.append("maintenanceCompany", updatedSC.maintenanceCompany);

  updatedSC.fileCatergoryInt &&
    formData.append("fileCatergoryInt", updatedSC.fileCatergoryInt);
  updatedSC.fileCatergory &&
    formData.append("fileCatergory", updatedSC.fileCatergory);
  updatedSC.fileName && formData.append("fileName", updatedSC.fileName);
  updatedSC.createdBy && formData.append("createdBy", updatedSC.createdBy);
  updatedSC.createdDate &&
    formData.append("createdDate", updatedSC.createdDate);
  updatedSC.modifiedBy && formData.append("modifiedBy", updatedSC.modifiedBy);
  updatedSC.modifiedDate &&
    formData.append("modifiedDate", updatedSC.modifiedDate);
  updatedSC.dbWarrantyVendorId &&
    formData.append("dbwarrantyVendorId", updatedSC.dbWarrantyVendorId);
  updatedSC.dbServicingVendorId &&
    formData.append("dbservicingVendorId", updatedSC.dbServicingVendorId);
  updatedSC.attachments &&
    updatedSC.attachments.map((x) => formData.append("attachments", x));
  updatedSC.attachments &&
    formData.append("attachments", updatedSC.attachments);

  const transformedSecCardReader = addFormattedHoursToGroups(
    updatedSC.secCardReader
  );
  updatedSC.secCardReader = transformedSecCardReader;

  updatedSC.secCardReader &&
    formData.append("secCardReader", JSON.stringify(updatedSC.secCardReader));

  updatedSC.initialAddCameras &&
    formData.append(
      "initialAddCameras",
      JSON.stringify(updatedSC.initialAddCameras)
    );

  updatedSC.initialAddAlarms &&
    formData.append(
      "initialAddAlarms",
      JSON.stringify(updatedSC.initialAddAlarms)
    );

  updatedSC.initialAddFireAlarmSystems &&
    formData.append(
      "initialAddFireAlarmSystems",
      JSON.stringify(updatedSC.initialAddFireAlarmSystems)
    );

  return await axios.post(`${api_base_url}/Security`, formData);
};

type valueType = {
  value: string | Blob;
};

//Edit Security
export const updateSecurity = async (newSC) => {
  const formData = new FormData();

  newSC.camerasAndLocation =
    newSC.camerasAndLocation &&
    newSC.camerasAndLocation.map((x) => x.value)?.join(",");
  newSC.keysAssignedTo =
    newSC.keysAssignedTo && newSC.keysAssignedTo.map((x) => x.value)?.join(",");
  newSC.officeId && formData.append("officeId", newSC.officeId);
  newSC.secCardReader &&
    formData.append("secCardReader", JSON.stringify(newSC.secCardReader));
  newSC.officeId && formData.append("officeId", newSC.officeId);
  newSC.officeState && formData.append("officeState", newSC.officeState);
  newSC.officeBrand && formData.append("officeBrand", newSC.officeBrand);
  newSC.officeStatus && formData.append("officeStatus", newSC.officeStatus);
  newSC.vendorId && formData.append("vendorId", newSC.vendorId);
  newSC.vendor && formData.append("vendor", newSC.vendor);
  newSC.vendorPhone && formData.append("vendorPhone", newSC.vendorPhone);
  newSC.contractRenewalDate &&
    formData.append("contractRenewalDate", newSC.contractRenewalDate);
  newSC.accountNumber && formData.append("accountNumber", newSC.accountNumber);
  newSC.alarmSiteNumber &&
    formData.append("alarmSiteNumber", newSC.alarmSiteNumber);
  newSC.dataConnectionId &&
    formData.append("dataConnectionId", newSC.dataConnectionId);
  newSC.alarmInstallDate &&
    formData.append("alarmInstallDate", newSC.alarmInstallDate);

  newSC.connectionDetails &&
    formData.append("connectionDetails", newSC.connectionDetails);
  newSC.alarmTypeId && formData.append("alarmTypeId", newSC.alarmTypeId);
  newSC.numberOfMotionSensors &&
    formData.append("numberOfMotionSensors", newSC.numberOfMotionSensors);
  newSC.numberOfDoorContacts &&
    formData.append("numberOfDoorContacts", newSC.numberOfDoorContacts);
  newSC.numberOfPanicButtons &&
    formData.append("numberOfPanicButtons", newSC.numberOfPanicButtons);
  newSC.specialEquipment &&
    formData.append("specialEquipment", newSC.specialEquipment);
  newSC.camerasAndLocation &&
    formData.append("camerasAndLocation", newSC.camerasAndLocation);
  newSC.cameraType1Id && formData.append("cameraType1Id", newSC.cameraType1Id);

  newSC.cameraType2Id && formData.append("cameraType2Id", newSC.cameraType2Id);
  newSC.cameraMake && formData.append("cameraMake", newSC.cameraMake);
  newSC.cameraInstallDate &&
    formData.append("cameraInstallDate", newSC.cameraInstallDate);
  newSC.cameraModel && formData.append("cameraModel", newSC.cameraModel);
  newSC.dvrtypeId && formData.append("dvrtypeId", newSC.dvrtypeId);
  newSC.dvrrecordingMax &&
    formData.append("dvrrecordingMax", newSC.dvrrecordingMax);
  newSC.cameraSystemId &&
    formData.append("cameraSystemId", newSC.cameraSystemId);
  newSC.safeTypeId && formData.append("safeTypeId", newSC.safeTypeId);
  newSC.isSafeBolted && formData.append("isSafeBolted", newSC.isSafeBolted);

  newSC.safeCode && formData.append("safeCode", newSC.safeCode);
  newSC.safeMakeId && formData.append("safeMakeId", newSC.safeMakeId);
  newSC.safeModel && formData.append("safeModel", newSC.safeModel);
  newSC.safeSerialNumber &&
    formData.append("safeSerialNumber", newSC.safeSerialNumber);
  newSC.safeLocation && formData.append("safeLocation", newSC.safeLocation);
  newSC.siteNumber && formData.append("siteNumber", newSC.siteNumber);
  newSC.connection && formData.append("connection", newSC.connection);
  newSC.installDate && formData.append("installDate", newSC.installDate);

  newSC.safeCode && formData.append("safeCode", newSC.safeCode);
  newSC.safeMakeId && formData.append("safeMakeId", newSC.safeMakeId);
  newSC.safeModel && formData.append("safeModel", newSC.safeModel);
  newSC.safeSerialNumber &&
    formData.append("safeSerialNumber", newSC.safeSerialNumber);
  newSC.safeLocation && formData.append("safeLocation", newSC.safeLocation);
  newSC.siteNumber && formData.append("siteNumber", newSC.siteNumber);
  newSC.connection && formData.append("connection", newSC.connection);
  newSC.installDate && formData.append("installDate", newSC.installDate);

  newSC.type && formData.append("type", newSC.type);
  newSC.numberOfButtonSelected &&
    formData.append("numberOfButtonSelected", newSC.numberOfButtonSelected);
  newSC.warranty && formData.append("warranty", newSC.warranty);
  newSC.warrantyLength &&
    formData.append("warrantyLength", newSC.warrantyLength);
  newSC.warrantyCoverage &&
    formData.append("warrantyCoverage", newSC.warrantyCoverage);
  newSC.warrantyCoverageId &&
    formData.append("warrantyCoverageId", newSC.warrantyCoverageId);
  newSC.typeOfVendorId &&
    formData.append("typeOfVendorId", newSC.typeOfVendorId);
  newSC.dbinstallVendorId &&
    formData.append("dbinstallVendorId", newSC.dbinstallVendorId);

  newSC.dbinstallVendorName &&
    formData.append("dbinstallVendorName", newSC.dbinstallVendorName);
  newSC.dbinstallVendorAddress &&
    formData.append("dbinstallVendorAddress", newSC.dbinstallVendorAddress);
  newSC.dbinstallVendorPhone &&
    formData.append("dbinstallVendorPhone", newSC.dbinstallVendorPhone);
  newSC.contact && formData.append("contact", newSC.contact);
  newSC.lockSmithVendor &&
    formData.append("lockSmithVendor", newSC.lockSmithVendor);
  newSC.lockSmithVendorId &&
    formData.append("lockSmithVendorId", newSC.lockSmithVendorId);
  newSC.okvendorAddress &&
    formData.append("okvendorAddress", newSC.okvendorAddress);
  newSC.okvendorPhone && formData.append("okvendorPhone", newSC.okvendorPhone);

  newSC.numberOfKeys && formData.append("numberOfKeys", newSC.numberOfKeys);
  newSC.keysAssignedTo &&
    formData.append("keysAssignedTo", newSC.keysAssignedTo);
  newSC.keyCodingModel &&
    formData.append("keyCodingModel", newSC.keyCodingModel);
  newSC.numberOfUnits && formData.append("numberOfUnits", newSC.numberOfUnits);
  newSC.unitType && formData.append("unitType", newSC.unitType);
  newSC.tonCapacity && formData.append("tonCapacity", newSC.tonCapacity);
  newSC.hvacinstallDate &&
    formData.append("hvacinstallDate", newSC.hvacinstallDate);
  newSC.preventiveMaintenancePlan &&
    formData.append(
      "preventiveMaintenancePlan",
      newSC.preventiveMaintenancePlan
    );

  newSC.maintenanceCompanyId &&
    formData.append("maintenanceCompanyId", newSC.maintenanceCompanyId);
  newSC.maintenanceCompany &&
    formData.append("maintenanceCompany", newSC.maintenanceCompany);
  newSC.fileCatergoryInt &&
    formData.append("fileCatergoryInt", newSC.fileCatergoryInt);
  newSC.fileCatergory && formData.append("fileCatergory", newSC.fileCatergory);
  newSC.fileName && formData.append("fileName", newSC.fileName);
  newSC.createdBy && formData.append("createdBy", newSC.createdBy);
  newSC.createdDate && formData.append("createdDate", newSC.createdDate);
  newSC.modifiedBy && formData.append("modifiedBy", newSC.modifiedBy);
  newSC.modifiedDate && formData.append("modifiedDate", newSC.modifiedDate);
  newSC.dbWarrantyVendorId &&
    formData.append("dbwarrantyVendorId", newSC.dbWarrantyVendorId);
  newSC.dbServicingVendorId &&
    formData.append("dbservicingVendorId", newSC.dbServicingVendorId);
  // newSC.attachments && formData.append("attachments", newSC.attachments);
  newSC.attachmentDisplay?.forEach((obj: ICategory, i: number) =>
    Object.entries(obj)?.forEach(([key, value]) => {
      if (value) {
        formData.append(`FileCatergoryDto[${i}].${key}`, value);
      }
    })
  );
  newSC.attachments &&
    newSC.attachments.map((x) => formData.append("attachments", x));

  newSC.attachmentsForDelete &&
    newSC.attachmentsForDelete.forEach((obj, i) => {
      const valueWithType: valueType = obj;
      Object.entries(valueWithType).forEach(([key, value]) => {
        formData.append(`attachmentsForDelete[${i}].${key}`, value);
        formData.append(
          `attachmentsForDelete[${i}].modifiedDate`,
          "2023-9-20T14:48:00"
        );
      });
    });
  newSC.onchangeCategory &&
    newSC.onchangeCategory.forEach((obj, i) => {
      const valueWithType: valueType = obj;
      Object.entries(valueWithType).forEach(([key, value]) => {
        formData.append(`onchangeCategory[${i}].${key}`, value);
        formData.append(
          `onchangeCategory[${i}].modifiedDate`,
          "2023-9-20T14:48:00"
        );
      });
    });
  return await axios.put(
    `${api_base_url}/Security/${newSC.recordNumber}`,
    formData
  );
};

export const getSecurityStatus = async () => {
  const data = await axios.get(`${api_base_url}/Common/SecurityStatus`);
  return data;
};

export const getSecurityType = async () => {
  const data = await axios.get(`${api_base_url}/Common/SecurityType`);
  return data;
};

export const getSecurityArea = async () => {
  const data = await axios.get(`${api_base_url}/Common/SecurityArea`);
  return data;
};

export const getDescriptiveGroup = async () => {
  const data = await axios.get(`${api_base_url}/Common/SecurityDescGroup`);
  return data;
};

export const getSecurityCloseReason = async () => {
  const data = await axios.get(`${api_base_url}/Common/SecurityCloseReason`);
  return data;
};

export const getSecurityException = async () => {
  const data = await axios.get(`${api_base_url}/Common/SecurityException`);
  return data;
};

export const getSecurityGeneralGroup = async () => {
  const data = await axios.get(`${api_base_url}/Common/SecurityGeneralGroup`);
  return data;
};

export const getSecurityBy = async (searchDetails) => {
  return await axios.post(
    `${api_base_url}/Security/SearchSecurity`,
    searchDetails
  );
};

export const searchSecurity = async (searchDetails) => {
  const res = await axios.post(
    `${api_base_url}/Security/searchDetails`,
    searchDetails
  );
  return res.data;
};

export const getSecurityByRecordNumber = async (recordNumber) => {
  let SecurityData;
  await axios
    .get(`${api_base_url}/Security/${recordNumber}`)
    .then((res) => (SecurityData = res.data));
  return SecurityData;
};

export const getSecurityForEdit = async (recordNumber) => {
  return await axios
    .get(`${api_base_url}/Security/ForEdit/${recordNumber}`)
    .then((res) => {
      return res.data;
    });
};

export const getSecurityByOfficeId = async (officeId) => {
  return await axios
    .get(`${api_base_url}/Security/ByOfficeId/${officeId}`)
    .then((res) => {
      return res.data;
    });
};

export const getSecurityByRecordNumbers = async (secIds) => {
  return await axios
    .post(`${api_base_url}/Security/GetSecurityByRecordnumbers`, secIds)
    .then((res) => {
      if (res) {
        return res.data;
      }
    });
};

export const getDataConnection = async () => {
  const data = await axios.get(`${api_base_url}/Security/DataConnection`);
  return data;
};

export const getAlarmType = async () => {
  const data = await axios.get(`${api_base_url}/Security/AlarmType`);
  return data;
};

export const getSelectedNumberOfButtons = async () => {
  const data = await axios.get(`${api_base_url}/Security/NumberOfButtons`);
  return data;
};

export const getNumberOfUnits = async () => {
  const data = await axios.get(`${api_base_url}/Security/NumberOfUnits`);
  return data;
};

export const getNumberOfKeys = async () => {
  const data = await axios.get(`${api_base_url}/Security/NumberOfKeys`);
  return data;
};

export const getCamerasAndLocation = async () => {
  const data = await axios.get(`${api_base_url}/Security/CamerasAndLocation`);
  return data;
};

export const getCameraTypes1 = async () => {
  const data = await axios.get(`${api_base_url}/Security/CameraTypes1`);
  return data;
};

export const getCameraTypes2 = async () => {
  const data = await axios.get(`${api_base_url}/Security/CameraTypes2`);
  return data;
};

export const getDVRTypes = async () => {
  const data = await axios.get(`${api_base_url}/Security/DVRTypes`);
  return data;
};

export const getSafeType = async () => {
  const data = await axios.get(`${api_base_url}/Security/SafeType`);
  return data;
};

export const getCameraSystems = async () => {
  const data = await axios.get(`${api_base_url}/Security/CameraSystems`);
  return data;
};

export const getSafeMakes = async () => {
  const data = await axios.get(`${api_base_url}/Security/SafeMakes`);
  return data;
};

export const getWarrantyCoverages = async () => {
  const data = await axios.get(`${api_base_url}/Security/WarrantyCoverages`);
  return data;
};
export const getSiteAdmin = async () => {
  const data = await axios.get(`${api_base_url}/Security/SiteAdmin`);
  return data;
};

export const getTypeOfVendors = async () => {
  const data = await axios.get(`${api_base_url}/Common/TypeOfVendors`);
  return data;
};

export const getFileCategories = async () => {
  const data = await axios.get(`${api_base_url}/Security/FileCategories`);
  return data;
};

export const getVendors = async () => {
  const data = await axios.get(`${api_base_url}/Common/Vendors`);
  return data;
};

export const getOfficesForSecurity = async () => {
  const data = await axios.get(`${api_base_url}/Security/Offices`);
  return data;
};

export const downloadSourceFile = async (file) => {
  var response = await axios.get(
    `${api_base_url}/Security/DownloadSourceFile?fileName=${file}`,
    {
      headers: {
        "Content-Type": "application/json",
      },
      responseType: "blob",
    }
  );
  return response;
};

export const getAlarmsByRecordNumber = async (recNumber) => {
  return await axios
    .get(`${api_base_url}/Security/Alarms/${recNumber}`)
    .then((res) => {
      return res.data;
    });
};

export const getFireAlarmSystemsByRecordNumber = async (recNumber) => {
  try {
    const data = await axios.get(`${api_base_url}/Security/FireAlarmSystems/${recNumber}`);

    if (data?.status === 500) {
      throw new Error(data.data || "Server returned an error");
    }

    return data.data;
  }
  catch (error: any) {
    const message = error.response?.data?.message || error.response?.data || error.message || "Unknown error";
    throw new Error(message);
  }
};

export const getCamerasByRecordNumber = async (recNumber) => {
  return await axios
    .get(`${api_base_url}/Security/Cameras/${recNumber}`)
    .then((res) => {
      return res.data;
    });
};

export const getCardReaderById = async (cardReaderData) => {
  let result =
    cardReaderData.id === undefined
      ? cardReaderData
      : await axios
        .get(`${api_base_url}/Security/CardReaderById/${cardReaderData.id}`)
        .then((res) => res.data);

  // Format date strings (YYYY-MM-DD)
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toISOString().split("T")[0];
  };

  if (result) {
    result.installDate = formatDate(result.installDate);
    result.cancellationDate = formatDate(result.cancellationDate);
    result.coverageStartDate = formatDate(result.coverageStartDate);
    result.coverageEndDate = formatDate(result.coverageEndDate);
    result.createdDate = formatDate(result.createdDate);
    result.modifiedDate = formatDate(result.modifiedDate);
  }

  if (result?.groupInfos?.length) {
    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    //format time into 24hrs
    const parseHours = (hoursStr) => {
      if (!hoursStr || !hoursStr.includes("-")) return { start: "", end: "" };
      const [startRaw, endRaw] = hoursStr.split("-").map((s) => s.trim());

      const start = moment(startRaw, ["h:mma", "hh:mma"]).format("HH:mm");
      const end = moment(endRaw, ["h:mma", "hh:mma"]).format("HH:mm");

      return { start, end };
    };

    result.groupInfos = result.groupInfos.map((grp) => {
      const updatedGrp = { ...grp };

      days.forEach((day) => {
        const { start, end } = parseHours(updatedGrp[`${day}_Hours`]);
        updatedGrp[`${day}StartTime`] = start;
        updatedGrp[`${day}EndTime`] = end;
      });

      return updatedGrp;
    });
  }
  return result;
};

export const getCardReaderByRecordNumber = async (cardReaderData) => {
  let result =
    cardReaderData.id === undefined
      ? cardReaderData
      : await axios
        .get(
          `${api_base_url}/Security/CardReaderByRecordNumber/${cardReaderData.id}`
        )
        .then((res) => {
          return res.data;
        });
  return result;
};

export const getCardReaderDetailsById = async (id) => {
  return await axios
    .get(`${api_base_url}/Security/CardReader/${id}`)
    .then((res) => {
      return res.data;
    });
};
//

export const getSafeDetailsByRecordNumber = async (recNumber) => {
  return await axios
    .get(`${api_base_url}/Security/SafeDetails/${recNumber}`)
    .then((res) => {
      return res.data;
    });
};
export const getDoorBuzzersByRecordNumber = async (recNumber) => {
  return await axios
    .get(`${api_base_url}/Security/DoorBuzzers/${recNumber}`)
    .then((res) => {
      return res.data;
    });
};
export const getHvacByRecordNumber = async (recNumber) => {
  return await axios
    .get(`${api_base_url}/Security/Hvacs/${recNumber}`)
    .then((res) => {
      return res.data;
    });
};

//Insert single cardreader
export const addSingleCardReader = async (cardReaderData) => {
  cardReaderData.siteAdmins = [
    ...cardReaderData.siteAdmins,
    ...cardReaderData.masterAdmin,
  ];
  return await axios.post(
    `${api_base_url}/Security/AddSingleCardReader`,
    cardReaderData
  );
};

//Edit single cardreader
export const editSingleCardReader = async (cardReaderData) => {
  cardReaderData.siteAdmins = [
    ...cardReaderData.siteAdmins,
    ...cardReaderData.masterAdmin,
  ];
  const data = await axios.put(
    `${api_base_url}/Security/UpdateSingleCardReader`,
    cardReaderData
  );
  return data;
};

//Delete CardReader
export const deleteCardReader = async (deleteCardReaderData) => {
  const data = await axios.put(
    `${api_base_url}/Security/DeleteSingleCardReader`,
    deleteCardReaderData
  );
  return data;
};

//Insert single alarm
export const addSingleAlarm = async (alarmData) => {
  return await axios.post(`${api_base_url}/Security/AddSingleAlarm`, alarmData);
};

//Edit single alarm
export const editSingleAlarm = async (alarmNewData) => {
  const data = await axios.put(
    `${api_base_url}/Security/UpdateSingleAlarm`,
    alarmNewData
  );
  return data;
};

//Delete alarm
export const deleteAlarm = async (deleteAlarmData) => {
  const data = await axios.put(
    `${api_base_url}/Security/DeleteAlarm`,
    deleteAlarmData
  );
  return data;
};


//Insert single fire alarm system
export const addSingleFireAlarmSystem = async (fireAlarmSystemData) => {
  try {
    const data = await axios.post(`${api_base_url}/Security/AddSingleFireAlarmSystem`, fireAlarmSystemData);

    if (data?.status === 500) {
      throw new Error(data.data || "Server returned an error");
    }

    return data;
  }
  catch (error: any) {
    const message = error.response?.data?.message || error.response?.data || error.message || "Unknown error";
    throw new Error(message);
  }
};

//Edit single fire alarm system
export const editSingleFireAlarmSystem = async (fireAlarmSystemNewData) => {
  try {
    const data = await axios.put(
      `${api_base_url}/Security/UpdateSingleFireAlarmSystem`,
      fireAlarmSystemNewData
    );

    if (data?.status === 500) {
      throw new Error(data.data || "Server returned an error");
    }

    return data;
  }
  catch (error: any) {
    const message = error.response?.data?.message || error.response?.data || error.message || "Unknown error";
    throw new Error(message);
  }
};

//Delete fire alarm system
export const deleteFireAlarmSystem = async (deleteFireAlarmSystemData) => {
  try {
    const data = await axios.put(
      `${api_base_url}/Security/DeleteFireAlarmSystem`,
      deleteFireAlarmSystemData
    );

    if (data?.status === 500) {
      throw new Error(data.data || "Server returned an error");
    }

    return data;
  }
  catch (error: any) {
    const message = error.response?.data?.message || error.response?.data || error.message || "Unknown error";
    throw new Error(message);
  }
};


//Insert single camera

interface CameraLocationDto {
  id: number;
  cameraId: null | number;
  camerasAndLocationId: string;
  cameraType1: number | null;
  cameraType2: number | null;
}

interface CameraDto {
  id: number;
  recordNumber: number;
  officeId: number;
  cameraMake: string;
  cameraModel: string;
  dvrTypeId: number | null;
  dvrRecordingMax: string;
  cameraInstallDate: string | null;
  isActive: boolean;
  createdBy: string;
  cameraSystemId: number | null;
  cameraType1: string | null;
  cameraType2: string | null;
  dvrType: string | null;
  cameraSystem: string | null;
  cameraLocationsDto: CameraLocationDto[];
}

export const addSingleCamera = async (cameraData: CameraDto): Promise<any> => {
  const formData = new FormData();

  Object.entries(cameraData).forEach(([key, value]) => {
    if (value !== null && value !== undefined && key !== "cameraLocationsDto") {
      if (Array.isArray(value)) {
        formData.append(key, value.join(","));
      } else {
        formData.append(key, value.toString());
      }
    }
  });

  if (
    cameraData.cameraLocationsDto &&
    Array.isArray(cameraData.cameraLocationsDto)
  ) {
    cameraData.cameraLocationsDto.forEach((locationGroup, index) => {
      let ids = locationGroup.camerasAndLocationId;

      if (Array.isArray(ids)) {
        ids = ids.join(",");
      }

      formData.append(
        `cameraLocationsDto[${index}].camerasAndLocationId`,
        ids.toString()
      );
      formData.append(
        `cameraLocationsDto[${index}].cameraType1`,
        locationGroup.cameraType1?.toString() || ""
      );
      formData.append(
        `cameraLocationsDto[${index}].cameraType2`,
        locationGroup.cameraType2?.toString() || ""
      );
    });
  }

  try {
    const response = await axios.post(
      `${api_base_url}/Security/AddSingleCamera/${cameraData.recordNumber}`,
      formData
    );
    return response;
  } catch (error) {
    throw error;
  }
};
//Edit single camera
export const editSingleCamera = async (cameraData: CameraDto): Promise<any> => {
  const formData = new FormData();

  Object.entries(cameraData).forEach(([key, value]) => {
    if (value !== null && value !== undefined && key !== "cameraLocationsDto") {
      if (Array.isArray(value)) {
        formData.append(key, value.join(","));
      } else {
        formData.append(key, value.toString());
      }
    }
  });

  // Append nested cameraLocationsDto
  if (
    cameraData.cameraLocationsDto &&
    Array.isArray(cameraData.cameraLocationsDto)
  ) {
    cameraData.cameraLocationsDto.forEach((locationGroup, index) => {
      formData.append(
        `cameraLocationsDto[${index}].camerasAndLocationId`,
        locationGroup.camerasAndLocationId?.toString() || ""
      );
      formData.append(
        `cameraLocationsDto[${index}].cameraType1`,
        locationGroup.cameraType1?.toString() || ""
      );
      formData.append(
        `cameraLocationsDto[${index}].cameraType2`,
        locationGroup.cameraType2?.toString() || ""
      );
      formData.append(
        `cameraLocationsDto[${index}].id`,
        locationGroup.id?.toString() || "0"
      );
      formData.append(
        `cameraLocationsDto[${index}].cameraId`,
        locationGroup.cameraId?.toString() || ""
      );
    });
  }

  return await axios.put(
    `${api_base_url}/Security/UpdateSingleCamera`,
    formData
  );
};

export const deleteCamera = async (deleteCameraData) => {
  const data = await axios.put(
    `${api_base_url}/Security/DeleteCamera`,
    deleteCameraData
  );
  return data;
};

//Insert single safe
export const addSingleSafe = async (safeData) => {
  return await axios.post(`${api_base_url}/Security/AddSingleSafe`, safeData);
};
//Edit single safe
export const editSingleSafe = async (safeNewData) => {
  const data = await axios.put(
    `${api_base_url}/Security/UpdateSingleSafe`,
    safeNewData
  );
  return data;
};
//Delete safe
export const deleteSafe = async (deleteSafeData) => {
  const data = await axios.put(
    `${api_base_url}/Security/DeleteSafe`,
    deleteSafeData
  );
  return data;
};
//Insert single doorBuzzer
export const addSingleDoorBuzzer = async (doorBuzzerData) => {
  return await axios.post(
    `${api_base_url}/Security/AddSingleDoorBuzzer`,
    doorBuzzerData
  );
};
//Edit single door buzzer
export const editSingleDoorBuzzer = async (doorBuzzerNewData) => {
  const data = await axios.put(
    `${api_base_url}/Security/UpdateSingleDoorBuzzer`,
    doorBuzzerNewData
  );
  return data;
};
//Delete door buzzer
export const deleteDoorBuzzer = async (deleteDoorBuzzerData) => {
  const data = await axios.put(
    `${api_base_url}/Security/DeleteDoorBuzzer`,
    deleteDoorBuzzerData
  );
  return data;
};
//Insert single havac
export const addSingleHvac = async (hvacData) => {
  return await axios.post(`${api_base_url}/Security/AddSingleHvac`, hvacData);
};
//Edit single hvac
export const editSingleHvac = async (hvacNewData) => {
  const data = await axios.put(
    `${api_base_url}/Security/UpdateSingleHvac`,
    hvacNewData
  );
  return data;
};
//Delete door hvac
export const deleteHvac = async (deleteHvacData) => {
  const data = await axios.put(
    `${api_base_url}/Security/DeleteHvac`,
    deleteHvacData
  );
  return data;
};
//Get Office Keys
export const getOfficeKeysByRecordNumber = async (recordNumber) => {
  return await axios
    .get(`${api_base_url}/Security/OfficeKeys/${recordNumber}`)
    .then((res) => {
      return res.data;
    });
};

//Insert Single Office Keys
export const addSingleOfficeKeys = async (officeKeyData) => {
  officeKeyData.keysAssignedTo =
    officeKeyData.keysAssignedTo &&
    officeKeyData.keysAssignedTo.map((x) => x.value)?.join(",");

  return await axios.post(
    `${api_base_url}/Security/AddSingleOfficeKey`,
    officeKeyData
  );
};

//Edit Single Office Key
export const editSingOfficeKeys = async (officeKey) => {
  const data = await axios.put(
    `${api_base_url}/Security/UpdateSingleOfficeKey`,
    officeKey
  );
  return data;
};

//Delete Office Key
export const deleteOfficeKey = async (deleteOfficeKeyData) => {
  const data = await axios.put(
    `${api_base_url}/Security/DeleteOfficeKey`,
    deleteOfficeKeyData
  );
  return data;
};

// Get Motion Censor
export const getMotionCensor = async () => {
  const data = await axios.get(`${api_base_url}/Security/MotionCensor`);
  return data;
};

// Get Fire Alarm Sensor Options
export const getFireAlarmSensorOptions = async () => {
  try {
    const data = await axios.get(`${api_base_url}/Security/FireAlarmSensorOptions`);

    if (data?.status === 500) {
      throw new Error(data.data || "Server returned an error");
    }

    return data;
  }
  catch (error: any) {
    const message = error.response?.data?.message || error.response?.data || error.message || "Unknown error";
    throw new Error(message);
  }
};

// Get Floor Count Options
export const getFloorCountOptions = async () => {
  try {
    const data = await axios.get(`${api_base_url}/Security/FloorCountOptions`);

    if (data?.status === 500) {
      throw new Error(data.data || "Server returned an error");
    }

    return data;
  }
  catch (error: any) {
    const message = error.response?.data?.message || error.response?.data || error.message || "Unknown error";
    throw new Error(message);
  }
};

// GetDoor Contact
export const getDoorContact = async () => {
  const data = await axios.get(`${api_base_url}/Security/DoorContact`);
  return data;
};

// Get Panic Button
export const getPanicButton = async () => {
  const data = await axios.get(`${api_base_url}/Security/PanicButton`);
  return data;
};

// Get Preventive Maintenance Plan
export const getPreventiveMaintenancePlan = async () => {
  const data = await axios.get(
    `${api_base_url}/Security/PreventiveMaintenancePlan`
  );
  return data;
};

//Get Security Count
export const getSecurityCount = async () => {
  return axios.get(`${api_base_url}/Security/GetSecurityCount/`).then((res) => {
    return res.data;
  });
};

// Get Requires Maintenance
export const getRequiresMaintenance = async () => {
  const data = await axios.get(`${api_base_url}/Security/RequiresMaintenance`);
  return data;
};

// Get Key Types
export const getKeyTypes = async () => {
  const data = await axios.get(`${api_base_url}/Security/KeyType`);
  return data;
};
